import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Button, Alert, Chip, Card, CardContent,
    Paper, LinearProgress, IconButton, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, Table, TableHead, TableRow, TableCell,
    TableBody, Collapse, Stack, useMediaQuery, useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
    Add, Edit, Delete, ExpandMore, ExpandLess,
    AccountBalance, TrendingDown, Schedule, CheckCircle,
} from "@mui/icons-material";
import { assetService } from "../../services/assetService";
import { AssetForm } from "../../components/assets/AssetForm";
import { Asset, AssetSummary, DepreciationEntry, AssetStatus } from "../../types/assets";
import { useAssetCategories } from "../../hooks/useAssetCategories";
import { formatCurrencyBDT } from "../../utils/currency";

// ── Status styling ────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<AssetStatus, "success" | "warning" | "default"> = {
    ACTIVE: "success",
    FULLY_DEPRECIATED: "warning",
    DISPOSED: "default",
};

const STATUS_LABEL: Record<AssetStatus, string> = {
    ACTIVE: "Active",
    FULLY_DEPRECIATED: "Fully Depreciated",
    DISPOSED: "Disposed",
};

// ── KPI card ──────────────────────────────────────────────────────────────────

interface KpiProps { label: string; value: string; sub?: string; icon: React.ReactNode; color: string }

const KpiCard: React.FC<KpiProps> = ({ label, value, sub, icon, color }) => (
    <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent sx={{ pb: "16px !important" }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 1.5 }, alignItems: "flex-start" }}>
                <Box sx={{ bgcolor: `${color}.light`, color: `${color}.dark`, p: 0.75, borderRadius: 1.5, display: "flex", flexShrink: 0 }}>
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>{value}</Typography>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ── Depreciation schedule dialog ──────────────────────────────────────────────

interface ScheduleDialogProps { asset: Asset | null; onClose: () => void }

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({ asset, onClose }) => {
    const [schedule, setSchedule] = useState<DepreciationEntry[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!asset) return;
        setLoading(true);
        assetService.getDepreciationSchedule(asset.asset_id)
            .then(setSchedule)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [asset]);

    const today = new Date();
    const currentMonthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;

    return (
        <Dialog open={!!asset} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Depreciation Schedule
                <Typography variant="body2" color="text.secondary">{asset?.name}</Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <LinearProgress />
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Month</TableCell>
                                <TableCell align="right">Monthly</TableCell>
                                <TableCell align="right">Accumulated</TableCell>
                                <TableCell align="right">Book Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedule.map((row) => {
                                const isNow = `${row.year}-${row.month}` === currentMonthKey;
                                return (
                                    <TableRow
                                        key={row.month_label}
                                        sx={isNow ? { bgcolor: "primary.50", fontWeight: 700 } : {}}
                                    >
                                        <TableCell>
                                            {row.month_label}
                                            {isNow && <Chip label="Now" size="small" color="primary" sx={{ ml: 1, height: 16, fontSize: "0.65rem" }} />}
                                        </TableCell>
                                        <TableCell align="right">{formatCurrencyBDT(row.monthly_amount)}</TableCell>
                                        <TableCell align="right">{formatCurrencyBDT(row.accumulated)}</TableCell>
                                        <TableCell align="right">{formatCurrencyBDT(row.book_value)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Pieces shared by the table row and the phone card ─────────────────────────

const DepreciationBar: React.FC<{ asset: Asset }> = ({ asset }) => (
    <>
        <Box sx={{ mb: 0.25 }}>
            <LinearProgress
                variant="determinate"
                value={Math.min(100, Number(asset.depreciation_pct))}
                sx={{
                    height: 6, borderRadius: 3, bgcolor: "grey.200",
                    "& .MuiLinearProgress-bar": {
                        bgcolor: Number(asset.depreciation_pct) >= 100 ? "warning.main" : "primary.main",
                        borderRadius: 3,
                    },
                }}
            />
        </Box>
        <Typography variant="caption" color="text.secondary">
            {Number(asset.depreciation_pct).toFixed(0)}% used
            {asset.months_remaining > 0 && ` · ${asset.months_remaining}mo left`}
        </Typography>
    </>
);

const AssetDetails: React.FC<{ asset: Asset }> = ({ asset }) => (
    <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Useful Life</Typography>
            <Typography variant="body2">{asset.useful_life_months} months</Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Salvage Value</Typography>
            <Typography variant="body2">{formatCurrencyBDT(asset.salvage_value)}</Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Accumulated Depreciation</Typography>
            <Typography variant="body2">{formatCurrencyBDT(asset.accumulated_depreciation)}</Typography>
        </Grid>
        {asset.vendor_name && (
            <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Vendor</Typography>
                <Typography variant="body2">{asset.vendor_name}</Typography>
            </Grid>
        )}
        {asset.serial_number && (
            <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Serial / Model</Typography>
                <Typography variant="body2">{asset.serial_number}</Typography>
            </Grid>
        )}
        {asset.description && (
            <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2">{asset.description}</Typography>
            </Grid>
        )}
    </Grid>
);

const AssetActions: React.FC<{ onEdit: () => void; onDelete: () => void; onSchedule: () => void }> = ({ onEdit, onDelete, onSchedule }) => (
    <>
        <Tooltip title="Depreciation schedule">
            <IconButton size="small" onClick={onSchedule} aria-label="Depreciation schedule"><Schedule fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit} aria-label="Edit"><Edit fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={onDelete} aria-label="Delete"><Delete fontSize="small" /></IconButton>
        </Tooltip>
    </>
);

// ── Asset row (expandable) ────────────────────────────────────────────────────

interface AssetRowProps {
    asset: Asset;
    onEdit: () => void;
    onDelete: () => void;
    onSchedule: () => void;
}

const AssetRow: React.FC<AssetRowProps> = ({ asset, onEdit, onDelete, onSchedule }) => {
    const [expanded, setExpanded] = useState(false);
    const { getCategoryMeta } = useAssetCategories();
    const catMeta = getCategoryMeta(asset.category);

    return (
        <>
            <TableRow hover sx={{ "& > td": { borderBottom: expanded ? "none" : undefined } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={600}>{asset.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{catMeta.label}</Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2">{formatCurrencyBDT(asset.purchase_cost)}</Typography>
                    <Typography variant="caption" color="text.secondary">{asset.purchase_date}</Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color={asset.book_value > 0 ? "success.main" : "text.disabled"}>
                        {formatCurrencyBDT(asset.book_value)}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2">{formatCurrencyBDT(asset.monthly_depreciation)}/mo</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                    <DepreciationBar asset={asset} />
                </TableCell>
                <TableCell>
                    <Chip
                        label={STATUS_LABEL[asset.status]}
                        color={STATUS_COLOR[asset.status]}
                        size="small"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell align="right">
                    <AssetActions onEdit={onEdit} onDelete={onDelete} onSchedule={onSchedule} />
                </TableCell>
            </TableRow>

            {/* Expandable detail row */}
            <TableRow>
                <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expanded} unmountOnExit>
                        <Box sx={{ px: 6, py: 1.5, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
                            <AssetDetails asset={asset} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// ── Asset card (phones) ───────────────────────────────────────────────────────

const AssetCard: React.FC<AssetRowProps> = ({ asset, onEdit, onDelete, onSchedule }) => {
    const [expanded, setExpanded] = useState(false);
    const { getCategoryMeta } = useAssetCategories();
    return (
        <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
            <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1.25}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={600}>{asset.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{getCategoryMeta(asset.category).label}</Typography>
                        </Box>
                        <Chip label={STATUS_LABEL[asset.status]} color={STATUS_COLOR[asset.status]} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Book value today</Typography>
                            <Typography variant="h6" fontWeight={700} color={asset.book_value > 0 ? "success.main" : "text.disabled"} sx={{ fontSize: "1.15rem", lineHeight: 1.3 }}>
                                {formatCurrencyBDT(asset.book_value)}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                            <Typography variant="caption" color="text.secondary">Bought {asset.purchase_date}</Typography>
                            <Typography variant="body2">{formatCurrencyBDT(asset.purchase_cost)}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatCurrencyBDT(asset.monthly_depreciation)}/mo</Typography>
                        </Box>
                    </Box>
                    <Box>
                        <DepreciationBar asset={asset} />
                    </Box>
                    <Collapse in={expanded} unmountOnExit>
                        <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1.5 }}>
                            <AssetDetails asset={asset} />
                        </Box>
                    </Collapse>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mx: -0.5 }}>
                        <Button size="small" onClick={() => setExpanded((v) => !v)} endIcon={expanded ? <ExpandLess /> : <ExpandMore />}>
                            {expanded ? "Less" : "Details"}
                        </Button>
                        <Box>
                            <AssetActions onEdit={onEdit} onDelete={onDelete} onSchedule={onSchedule} />
                        </Box>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────

export const AssetList: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [assets, setAssets] = useState<Asset[]>([]);
    const [summary, setSummary] = useState<AssetSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");

    const [formOpen, setFormOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [scheduleAsset, setScheduleAsset] = useState<Asset | null>(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const [assetList, sum] = await Promise.all([
                assetService.listAssets(statusFilter ? { status: statusFilter } : {}),
                assetService.getSummary(),
            ]);
            setAssets(assetList);
            setSummary(sum);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to load assets");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleDelete = async (asset: Asset) => {
        if (!window.confirm(`Delete "${asset.name}"? This cannot be undone.`)) return;
        try {
            await assetService.deleteAsset(asset.asset_id);
            loadData();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to delete asset");
        }
    };

    const STATUS_FILTERS = [
        { key: "", label: "All" },
        { key: "ACTIVE", label: "Active" },
        { key: "FULLY_DEPRECIATED", label: "Fully Depreciated" },
        { key: "DISPOSED", label: "Disposed" },
    ];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700}>Assets</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Fixed asset register — tracks capital purchases and their depreciation over time.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} sx={{ flexShrink: 0, whiteSpace: "nowrap" }} onClick={() => { setSelectedAsset(null); setFormMode("create"); setFormOpen(true); }}>
                    {isMobile ? "Add" : "Add Asset"}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

            {/* KPI cards */}
            {summary && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Total Assets"
                            value={String(summary.total_count)}
                            sub={`${summary.active_count} active`}
                            icon={<AccountBalance fontSize="small" />}
                            color="primary"
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Total Book Value"
                            value={formatCurrencyBDT(summary.total_book_value)}
                            sub={`of ${formatCurrencyBDT(summary.total_purchase_cost)} purchased`}
                            icon={<CheckCircle fontSize="small" />}
                            color="success"
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Monthly Depreciation"
                            value={formatCurrencyBDT(summary.monthly_depreciation_burden)}
                            sub="ongoing monthly cost from active assets"
                            icon={<TrendingDown fontSize="small" />}
                            color="warning"
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Fully Depreciated"
                            value={String(summary.fully_depreciated_count)}
                            sub={`${summary.disposed_count} disposed`}
                            icon={<Schedule fontSize="small" />}
                            color="info"
                        />
                    </Grid>
                </Grid>
            )}

            {/* Status filter */}
            <Box sx={{ display: "flex", gap: 0.75, mb: 2, flexWrap: "wrap" }}>
                {STATUS_FILTERS.map((f) => (
                    <Chip
                        key={f.key}
                        label={f.label}
                        size="small"
                        onClick={() => setStatusFilter(f.key)}
                        color={statusFilter === f.key ? "primary" : "default"}
                        variant={statusFilter === f.key ? "filled" : "outlined"}
                    />
                ))}
            </Box>

            {/* Cards on a phone, table elsewhere */}
            {isMobile ? (
                <Box>
                    {loading && <LinearProgress sx={{ mb: 1.5 }} />}
                    {assets.length === 0 && !loading ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary" variant="body2">
                                No assets yet. Add your first asset to start tracking depreciation.
                            </Typography>
                        </Box>
                    ) : (
                        assets.map((asset) => (
                            <AssetCard
                                key={asset.asset_id}
                                asset={asset}
                                onEdit={() => { setSelectedAsset(asset); setFormMode("edit"); setFormOpen(true); }}
                                onDelete={() => handleDelete(asset)}
                                onSchedule={() => setScheduleAsset(asset)}
                            />
                        ))
                    )}
                </Box>
            ) : (
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                {loading && <LinearProgress />}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={40} />
                            <TableCell>Asset</TableCell>
                            <TableCell align="right">Purchase Cost</TableCell>
                            <TableCell align="right">Book Value (Today)</TableCell>
                            <TableCell align="right">Monthly Cost</TableCell>
                            <TableCell sx={{ minWidth: 140 }}>Depreciation</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {assets.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary" variant="body2">
                                        No assets yet. Add your first asset to start tracking depreciation.
                                    </Typography>
                                    <Button
                                        variant="outlined" size="small" startIcon={<Add />} sx={{ mt: 1.5 }}
                                        onClick={() => { setSelectedAsset(null); setFormMode("create"); setFormOpen(true); }}
                                    >
                                        Add First Asset
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ) : (
                            assets.map((asset) => (
                                <AssetRow
                                    key={asset.asset_id}
                                    asset={asset}
                                    onEdit={() => { setSelectedAsset(asset); setFormMode("edit"); setFormOpen(true); }}
                                    onDelete={() => handleDelete(asset)}
                                    onSchedule={() => setScheduleAsset(asset)}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>
            )}

            {/* Dialogs */}
            <AssetForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSuccess={() => { setFormOpen(false); loadData(); }}
                asset={selectedAsset}
                mode={formMode}
            />

            <ScheduleDialog
                asset={scheduleAsset}
                onClose={() => setScheduleAsset(null)}
            />
        </Box>
    );
};
